import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { HERO_SCROLL_MOTION_QUERY } from 'hooks/useHeroScrollMotion';

const MAX_SHIFT_RATIO = 0.18;
const MAX_SHIFT_PX = 180;
const MIN_SHIFT_PX = 96;
const MAX_SCALE = 1.22;
const DESKTOP_TEXT_SHIFT_PERCENT = 52;

export default function HeroParallaxController() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia(HERO_SCROLL_MOTION_QUERY);
    let frame = 0;
    let routeFrame = 0;
    let routeTimer = 0;

    const update = () => {
      frame = 0;
      const nativeScroll = !mediaQuery.matches;
      document.querySelectorAll('[data-hero-parallax="true"]').forEach((element) => {
        if (nativeScroll) {
          element.style.transform = '';
          element.style.willChange = '';
          return;
        }

        const section = element.closest('section') || element.parentElement;
        if (!section) return;

        const rect = section.getBoundingClientRect();
        const progress = Math.min(1, Math.max(0, -rect.top / Math.max(1, rect.height)));
        const maxShift = Math.min(MAX_SHIFT_PX, Math.max(MIN_SHIFT_PX, rect.height * MAX_SHIFT_RATIO));
        const shift = progress * maxShift;
        const scale = 1.02 + progress * (MAX_SCALE - 1.02);

        element.style.transform = `translate3d(0, ${shift}px, 0) scale(${scale})`;
        element.style.transformOrigin = 'center center';
        element.style.willChange = 'transform';
      });
      document.querySelectorAll('[data-hero-text-parallax="true"]').forEach((element) => {
        if (nativeScroll) {
          element.style.transform = '';
          element.style.opacity = '';
          element.style.willChange = '';
          return;
        }

        const section = element.closest('section') || element.parentElement;
        if (!section) return;

        const rect = section.getBoundingClientRect();
        const progress = Math.min(1, Math.max(0, -rect.top / Math.max(1, rect.height)));
        const opacity = Math.max(0, 1 - (progress / 0.72));

        element.style.transform = `translate3d(0, ${progress * DESKTOP_TEXT_SHIFT_PERCENT}%, 0)`;
        element.style.opacity = String(opacity);
        element.style.willChange = 'transform, opacity';
      });
    };

    const requestUpdate = () => {
      if (frame || !mediaQuery.matches) return;
      frame = window.requestAnimationFrame(update);
    };

    const stopUpdates = () => {
      if (frame) window.cancelAnimationFrame(frame);
      if (routeFrame) window.cancelAnimationFrame(routeFrame);
      if (routeTimer) window.clearTimeout(routeTimer);
      frame = routeFrame = routeTimer = 0;
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
    const configureMotion = () => {
      stopUpdates();
      update();
      if (!mediaQuery.matches) return;
      routeFrame = window.requestAnimationFrame(update);
      routeTimer = window.setTimeout(update, 90);
      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate);
    };

    configureMotion();
    mediaQuery.addEventListener?.('change', configureMotion);

    return () => {
      stopUpdates();
      mediaQuery.removeEventListener?.('change', configureMotion);
    };
  }, [location.pathname, location.search]);

  return null;
}
